import React from 'react';

import {StorableItemType} from 'constants/ModelTypeConstants';
import {NavStoreRegistry, Registry} from 'constants/RegistryConstants';
import {GRID_CLASSES, AccessibleGridCell, ModalPane, SelectedParentStorable} from 'constants/SampleConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {setDefaultRegistry} from 'registry/DefaultRegistry';
import RegistrySelector from 'registry/RegistrySelector';
import CreateBoxPane, {CreateBoxPaneState} from 'samples/CreateBoxPane';
import CreatePlatePane, {CreatePlatePaneState} from 'samples/CreatePlatePane';
import GridCellsSelectorContainer from 'samples/grids/GridCellsSelectorContainer';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {ParentStorableSelector} from '../ParentStorableDataSelector';
import * as SampleUtil from '../SampleUtil';

interface SelectParentGridPaneProps {
  getParentStorableSelectorError: (paneState: SelectParentStorablePaneState) => string | null;
  paneState: SelectParentGridPaneState;
  onUpdate: (state: Partial<SelectParentGridPaneState>) => void;
  selectedPane: ModalPane;
  storableSchema?: StorableSchema;
  numRegistries: number;
  registries: Array<NavStoreRegistry>;
}

export interface SelectParentStorablePaneState {
  selectedParent: SelectedParentStorable;
  registry: Registry | null;
  shouldValidateSelectedParent: boolean;
}

export const DEFAULT_SELECT_PARENT_STORABLE_STATE: SelectParentStorablePaneState = {
  selectedParent: getEmptySelectedParent(),
  registry: null,
  shouldValidateSelectedParent: false,
};

export type SelectParentGridPaneState =
  | SelectParentStorablePaneState
  | CreatePlatePaneState
  | CreateBoxPaneState;

/**
 * Select or create a parent grid, without choosing grid cells.
 */
class SelectParentGridPane extends React.Component<SelectParentGridPaneProps> {
  override componentDidMount(): void {
    FileBrowserActions.fetchSources();
  }

  override componentDidUpdate(prevProps: SelectParentGridPaneProps): void {
    if (this.props.paneState.registry?.id) {
      setDefaultRegistry(this.props.paneState.registry.id);
    }
  }

  _handleRegistryChange = (e: {target: {value: Registry}}): void => {
    this.props.onUpdate({registry: e.target.value});
  };

  _handleUpdateCreatePane = (state: Partial<CreateBoxPaneState | CreatePlatePaneState>): void => {
    this.props.onUpdate(state);
  };

  _handleSelect = (selectedParent: {
    storable: StorableSummaryGraphQL;
    gridCells: Array<AccessibleGridCell>;
  }): void => {
    this.props.onUpdate({selectedParent});
  };

  _handleRemove = (): void => {
    this.props.onUpdate({selectedParent: getEmptySelectedParent()});
  };

  override render(): JSX.Element {
    if (!this.props.registries) {
      // Block the UI until registries are loaded because certain subcomponents (e.g. CreatePlatePane) can
      // enter error states while registries are loading.
      return <CenteredHorizontalThrobber />;
    }
    if (this.props.selectedPane === ModalPane.SELECT_PARENT_STORABLE) {
      const paneState = this.props.paneState as SelectParentStorablePaneState;
      // We only need a registry selector when we don't have a storableSchema (otherwise the storableSchema's
      // registryId will be used)
      const registrySelectorSection = (
        <Row>
          <Col md={6}>
            <FieldLabel label="Registry" labelStyle={FieldLabel.STYLES.LABEL} isRequired={true} />
            <RegistrySelector onChange={this._handleRegistryChange} registryId={paneState.registry?.id} />
          </Col>
        </Row>
      );
      return (
        <div>
          {this.props.storableSchema == null && registrySelectorSection}
          <Row>
            <Col md={12}>
              <ParentStorableSelector
                autoFocus={true}
                error={
                  paneState.shouldValidateSelectedParent
                    ? this.props.getParentStorableSelectorError(paneState)
                    : null
                }
                isRequired={false}
                onRemove={this._handleRemove}
                onSelect={this._handleSelect}
                parentStorableTypesOverride={GRID_CLASSES as unknown as Array<StorableItemType>}
                selectedParent={paneState.selectedParent}
                storableSchema={this.props.storableSchema}
                registryId={this.props.storableSchema?.registryId ?? paneState.registry?.id}
              />
              {Boolean(
                paneState.selectedParent &&
                paneState.selectedParent.storable &&
                SampleUtil.isGridId(paneState.selectedParent.storable.id)
              ) && (
                <GridCellsSelectorContainer
                  allowSelectingCells={false}
                  gridId={paneState.selectedParent.storable!.id}
                  selectedCells={[]}
                />
              )}
            </Col>
          </Row>
        </div>
      );
    } else if (this.props.selectedPane === ModalPane.CREATE_PLATE) {
      const paneState = this.props.paneState as CreatePlatePaneState;
      return (
        <CreatePlatePane
          {...paneState}
          numRegistries={this.props.numRegistries}
          onUpdate={this._handleUpdateCreatePane}
          registryId={paneState.registry?.id}
        />
      );
    } else {
      // selected pane is ModalPane.CREATE_BOX
      const paneState = this.props.paneState as CreateBoxPaneState;
      return (
        <CreateBoxPane
          {...paneState}
          numRegistries={this.props.numRegistries}
          onUpdate={this._handleUpdateCreatePane}
          registryId={paneState.registry?.id}
        />
      );
    }
  }
}

export default SelectParentGridPane;
